import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import classNames from "classnames";
import { translateBackendErrorCode } from "@jti/fe-common";
import { CasPurposesApi, OmpApi } from "@jti/rest-api";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { Button } from "../../components/Button";
import { Form } from "../../components/Form";
import { Recaptchabar } from "../../components/Recaptchabar";
import { Toaster } from "../../components/Toaster";
import { useConfig } from "../../providers/useConfig";
import { useTranslations } from "../../providers/useTranslations";
import { SuccessScreen } from "./components/SuccessScreen/SuccessScreen";
import { getConvertedChildrenDaraData, getSuccessFields, removeWhitespacesFromPhoneNumber, replaceSelectValuesWithString, transformDataWithConsents, updateCheckboxValue, } from "./helpers/helpers";
import { useUserProfile } from "./hooks/useUserProfile";
import styles from "./ContactForm.module.scss";
export const ContactForm = ({ children, fields, sendButtonLabel, description, title, hiddenFields, serverErrorMessage, formDisclaimer, successScreenTitle, successScreenButtonLabel, successScreenAlertButtonLabel, successScreenHref, successScreenDescription, isPrefilled = false, isPrefilledDisabled = false, preFilledFields, onSuccess = null, className = "", }) => {
    const userProfile = useUserProfile();
    const [convertedChildrenData, setConvertedChildrenData] = useState(children);
    const { reCaptchaEnabled, brandingInformationEnabled } = useConfig();
    const mappedHiddenFields = hiddenFields?.reduce((acc, field) => {
        acc[field.key] = field.value;
        return acc;
    }, {});
    const defaultValues = {
        ...fields?.reduce((acc, field) => {
            if (field.checked) {
                acc[field.id] = true;
                return acc;
            }
            acc[field.id] = "";
            return acc;
        }, {}),
        ...mappedHiddenFields,
    };
    const form = useForm({
        mode: "onTouched",
        defaultValues,
    });
    const { reset, trigger, getValues } = form;
    const [consent, setConsent] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showSuccessScreen, setShowSuccessScreen] = useState(false);
    const [successFields, setSuccessFields] = useState([]);
    const { __ } = useTranslations();
    const onSubmit = async () => {
        const values = getValues();
        setIsLoading(true);
        trigger(Object.keys(values));
        // Temporary solution before DESP-69708 goes live and all TextInputs used for phone number can be replaced with InternationalPhoneInput
        const processedValues = updateCheckboxValue(removeWhitespacesFromPhoneNumber(values));
        try {
            const responseData = await OmpApi.init().sendMessage(transformDataWithConsents(replaceSelectValuesWithString(processedValues), consent));
            setIsLoading(false);
            dispatchTrackingEvent(TrackingEvents.TrackingEvent, { event: "contact_form_submit" });
            if (responseData?.errors && serverErrorMessage) {
                toast.error(serverErrorMessage);
                return;
            }
            const successFields = getSuccessFields(fields, processedValues);
            setSuccessFields(successFields);
            setShowSuccessScreen(true);
            if (onSuccess) {
                onSuccess();
            }
            reset();
        }
        catch (error) {
            setIsLoading(false);
            console.error(error);
            const translatedError = translateBackendErrorCode(error?.error_code);
            const errorMessage = translatedError || serverErrorMessage;
            if (errorMessage) {
                toast.error(errorMessage);
            }
        }
    };
    useEffect(() => {
        CasPurposesApi.init()
            .getPurposesByStep("contact_form")
            .then((response) => {
            setConsent(response);
        });
    }, []);
    useEffect(() => {
        if (isPrefilled && !!userProfile && !!preFilledFields) {
            const convertedChildrenData = getConvertedChildrenDaraData(children, preFilledFields, isPrefilledDisabled);
            setConvertedChildrenData(convertedChildrenData);
            const { surnameFieldName, nameFieldName, emailFieldName, confirmEmailFieldName, phoneNumberFieldName } = preFilledFields;
            reset({
                ...(!!nameFieldName && { [nameFieldName]: userProfile.firstname }),
                ...(!!surnameFieldName && { [surnameFieldName]: userProfile.lastname }),
                ...(!!emailFieldName && { [emailFieldName]: userProfile.email }),
                ...(!!confirmEmailFieldName && { [confirmEmailFieldName]: userProfile.email }),
                ...(!!phoneNumberFieldName && { [phoneNumberFieldName]: userProfile.telephone }),
                ...mappedHiddenFields,
            });
        }
    }, [isPrefilled, reset, !!userProfile, !!preFilledFields]);
    return (_jsxs("div", { className: classNames(styles.formWrapper, className), children: [_jsx("h2", { "data-testid": "contact-form-title", className: styles.title, children: title }), _jsx("p", { "data-testid": "contact-form-description", className: styles.description, children: description }), showSuccessScreen ? (_jsx(SuccessScreen, { successScreenTitle: successScreenTitle, successScreenButtonLabel: successScreenButtonLabel, successScreenAlertButtonLabel: successScreenAlertButtonLabel, successFields: successFields, successScreenHref: successScreenHref, successScreenDescription: successScreenDescription })) : (_jsxs(_Fragment, { children: [_jsx(Toaster, {}), _jsxs(Form, { dataTestId: "contactForm", form: form, onSubmit: onSubmit, children: [convertedChildrenData, _jsx("p", { className: styles.footerDisclaimer, children: formDisclaimer }), _jsx("div", { className: styles.buttonWrapper, children: _jsx(Button, { disabled: !form.formState.isValid, "data-testid": "send-button", type: "submit", isPending: isLoading, children: sendButtonLabel || __("contact.button.send") }) })] }), reCaptchaEnabled && brandingInformationEnabled && _jsx(Recaptchabar, {})] }))] }));
};
//# sourceMappingURL=ContactForm.js.map